import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Typography, 
  Button, 
  Stack, 
  Snackbar, 
  Alert, 
  Divider, 
  Grid, 
  Switch, 
  FormControlLabel, 
  CardHeader,
  Paper,
  IconButton,
  Tab,
  Tabs,
  AppBar
} from '@mui/material';
import { Save as SaveIcon, Settings as SettingsIcon, Brush as BrushIcon, FormatListNumbered as NumberingIcon } from '@mui/icons-material';
import { getBranding as apiGetBranding, updateBranding as apiUpdateBranding, getNumberingSettings, updateNumberingSettings, getReportReferences, updateReportReferences } from '../services/settings';
import api from '../services/api';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const AdminSettingsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // Branding state
  const [brandingForm, setBrandingForm] = useState({
    companyName: '',
    clientLogo: '',
    pdfLogo: '',
    pdfLogoTenes: '',
    pdfHeader: { city: '', dateLabel: '' },
    pdfHeaderTenes: { city: '', dateLabel: '' },
    leaveAccepted: { pvBullet: '', regulatoryBullet: '' },
    countWeekends: false,
    countHolidays: false,
  });

  // Numbering state
  const [numberingForm, setNumberingForm] = useState({
    nextMissionOrderNumber: 1,
    nextLeaveReferenceNumber: 1,
    nextContractNumber: 1,
    numerotationAutomatique: '0001/DG/2MPI/2025',
    reportReferences: {
      attestation_travail: 1,
      certificat_travail: 1,
      decision: 1
    }
  });

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const autoSaveRef = useRef(null);

  useEffect(() => {
    loadBranding();
    loadNumbering();
  }, []);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const res = await apiGetBranding();
      const data = res.data || {};
      setBrandingForm({
        companyName: data.companyName || '',
        clientLogo: data.clientLogo || '',
        pdfLogo: data.pdfLogo || '',
        pdfLogoTenes: data.pdfLogoTenes || '',
        pdfHeader: { city: data?.pdfHeader?.city || '', dateLabel: data?.pdfHeader?.dateLabel || '' },
        pdfHeaderTenes: { city: data?.pdfHeaderTenes?.city || '', dateLabel: data?.pdfHeaderTenes?.dateLabel || '' },
        leaveAccepted: { pvBullet: data?.leaveAccepted?.pvBullet || '', regulatoryBullet: data?.leaveAccepted?.regulatoryBullet || '' },
        countWeekends: !!data.countWeekends,
        countHolidays: !!data.countHolidays,
      });
    } catch (e) {
      showNotification("Impossible de charger les paramètres de branding", 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadNumbering = async () => {
    try {
      // Charger les paramètres de numérotation existants
      const numberingResponse = await getNumberingSettings();
      
      // Charger les références des rapports depuis ReferenceCounters
      const reportReferencesResponse = await getReportReferences();
      
      setNumberingForm({
        ...numberingResponse.data,
        reportReferences: reportReferencesResponse.data
      });
    } catch (error) {
      console.error('Error loading numbering settings:', error);
      showNotification('Erreur lors du chargement des paramètres de numérotation', 'error');
    }
  };

  const onBrandingChange = (field, value) => {
    setBrandingForm((f) => ({ ...f, [field]: value }));
  };

  const onHeaderChange = (field, value) => {
    setBrandingForm((f) => ({ ...f, pdfHeader: { ...(f.pdfHeader || {}), [field]: value } }));
  };

  const onHeaderTenesChange = (field, value) => {
    setBrandingForm((f) => ({ ...f, pdfHeaderTenes: { ...(f.pdfHeaderTenes || {}), [field]: value } }));
  };

  const onLeaveAcceptedTplChange = (field, value) => {
    setBrandingForm((f) => ({ ...f, leaveAccepted: { ...(f.leaveAccepted || {}), [field]: value } }));
  };

  const onNumberingChange = (field) => (event) => {
    const value = parseInt(event.target.value) || 0;
    setNumberingForm(prev => ({
      ...prev,
      [field]: value > 0 ? value : 1
    }));
  };

  const onReportReferenceChange = (reportType) => (event) => {
    const value = parseInt(event.target.value) || 0;
    setNumberingForm(prev => ({
      ...prev,
      reportReferences: {
        ...prev.reportReferences,
        [reportType]: value > 0 ? value : 1
      }
    }));
  };

  // Auto-save leaveAccepted templates with debounce
  useEffect(() => {
    if (!brandingForm.leaveAccepted) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      try {
        await apiUpdateBranding(brandingForm);
        showNotification('Modèles de congé enregistrés', 'success');
      } catch (e) {
        showNotification("Échec de l'enregistrement des modèles", 'error');
      }
    }, 600);
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, [brandingForm.leaveAccepted]);

  const uploadImg = async (file) => {
    if (!file) return '';
    const fd = new FormData();
    fd.append('files', file);
    const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.filePaths.split(',')[0];
  };

  const handleUpload = async (targetKey) => {
    try {
      const input = document.getElementById(`file-${targetKey}`);
      if (!input || !input.files[0]) return;
      const url = await uploadImg(input.files[0]);
      if (targetKey === 'clientLogo') onBrandingChange('clientLogo', url);
      if (targetKey === 'pdfLogo') onBrandingChange('pdfLogo', url);
      if (targetKey === 'pdfLogoTenes') onBrandingChange('pdfLogoTenes', url);
      showNotification('Image importée', 'success');
    } catch (e) {
      showNotification("Import d'image échoué", 'error');
    }
  };

  const saveBranding = async () => {
    try {
      setLoading(true);
      await apiUpdateBranding(brandingForm);
      showNotification('Branding enregistré', 'success');
    } catch (e) {
      showNotification("Échec de l'enregistrement du branding", 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveNumbering = async () => {
    setLoading(true);
    try {
      console.log('🔍 AdminSettings - numberingForm being saved:', numberingForm);
      console.log('🔍 AdminSettings - nextContractNumber:', numberingForm.nextContractNumber);
      console.log('🔍 AdminSettings - reportReferences:', numberingForm.reportReferences);
      
      // Sauvegarder les paramètres de numérotation
      await updateNumberingSettings({
        nextMissionOrderNumber: numberingForm.nextMissionOrderNumber,
        nextLeaveReferenceNumber: numberingForm.nextLeaveReferenceNumber,
        nextContractNumber: numberingForm.nextContractNumber
      });
      
      // Sauvegarder les références des rapports (incluant le contrat!)
      const reportData = {
        ...numberingForm.reportReferences,
        contract: numberingForm.nextContractNumber
      };
      
      console.log('📤 AdminSettings - Report data being sent:', reportData);
      console.log('📤 AdminSettings - Contract value being sent:', reportData.contract);
      await updateReportReferences(reportData);
      
      showNotification('Paramètres de numérotation enregistrés', 'success');
    } catch (error) {
      console.error('Error saving numbering settings:', error);
      if (error.response?.data?.message) {
        showNotification(error.response.data.message, 'error');
      } else {
        showNotification('Erreur lors de l\'enregistrement des paramètres de numérotation', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async () => {
    await Promise.all([saveBranding(), saveNumbering()]);
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Paramètres
      </Typography>

      <AppBar position="static" color="default" elevation={1}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Paramètres tabs"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab 
            icon={<BrushIcon />} 
            label="Branding & Apparence" 
            iconPosition="start"
          />
          <Tab 
            icon={<NumberingIcon />} 
            label="Numérotation" 
            iconPosition="start"
          />
        </Tabs>
      </AppBar>

      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardHeader title="Identité" subheader="Nom de l'entreprise et logos" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nom de l'entreprise" value={brandingForm.companyName} onChange={(e) => onBrandingChange('companyName', e.target.value)} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Affiché dans l'en-tête et/ou documents.</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>Modèles de texte - Titre de congé</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Placeholders: {'{pvNumber} {pvDate} {pvPerson} {companyName} {exerciseLabel} {pvNumberOpt} {pvDateOpt} {pvPersonOpt} {exerciseLabelOpt}'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ligne PV (procès-verbal)"
                  placeholder={"– Vu le procès-verbal de nomination{pvNumberOpt}{pvDateOpt}{pvPersonOpt} en qualité du président du Conseil d'Administration de la société {companyName}."}
                  value={brandingForm.leaveAccepted.pvBullet}
                  onChange={(e) => onLeaveAcceptedTplChange('pvBullet', e.target.value)}
                  helperText="Écrivez la ligne complète. Elle sera appliquée immédiatement."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ligne réglementaire"
                  placeholder={"– Vu les dispositions règlementaires."}
                  value={brandingForm.leaveAccepted.regulatoryBullet}
                  onChange={(e) => onLeaveAcceptedTplChange('regulatoryBullet', e.target.value)}
                  helperText="Écrivez la ligne complète. Elle sera appliquée immédiatement."
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Logo du tableau de bord</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="URL Logo Dashboard" value={brandingForm.clientLogo} onChange={(e) => onBrandingChange('clientLogo', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <input type="file" id="file-clientLogo" accept="image/*" />
                  <Button onClick={() => handleUpload('clientLogo')} disabled={loading}>Uploader</Button>
                </Stack>
              </Grid>
              {brandingForm.clientLogo && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1, border: '1px solid #eee', display: 'inline-block', borderRadius: 1 }}>
                    <img src={brandingForm.clientLogo} alt="client" style={{ maxHeight: 80 }} />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>En-tête pour PDF</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="URL Logo PDF" value={brandingForm.pdfLogo} onChange={(e) => onBrandingChange('pdfLogo', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <input type="file" id="file-pdfLogo" accept="image/*" />
                  <Button onClick={() => handleUpload('pdfLogo')} disabled={loading}>Uploader</Button>
                </Stack>
              </Grid>
              {brandingForm.pdfLogo && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1, border: '1px solid #eee', display: 'inline-block', borderRadius: 1 }}>
                    <img src={brandingForm.pdfLogo} alt="pdf" style={{ maxHeight: 80 }} />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>En-tête pour PDF - Site Tenes</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="URL Logo PDF - Tenes" value={brandingForm.pdfLogoTenes} onChange={(e) => onBrandingChange('pdfLogoTenes', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <input type="file" id="file-pdfLogoTenes" accept="image/*" />
                  <Button onClick={() => handleUpload('pdfLogoTenes')} disabled={loading}>Uploader</Button>
                </Stack>
              </Grid>
              {brandingForm.pdfLogoTenes && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1, border: '1px solid #eee', display: 'inline-block', borderRadius: 1 }}>
                    <img src={brandingForm.pdfLogoTenes} alt="pdf-tenes" style={{ maxHeight: 80 }} />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>En-tête des documents PDF</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Ville (en-tête PDF)" value={brandingForm.pdfHeader.city} onChange={(e) => onHeaderChange('city', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Libellé date (en-tête PDF)" value={brandingForm.pdfHeader.dateLabel} onChange={(e) => onHeaderChange('dateLabel', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>En-tête des documents PDF - Site Tenes</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Ville (en-tête PDF - Tenes)" value={brandingForm.pdfHeaderTenes.city} onChange={(e) => onHeaderTenesChange('city', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Libellé date (en-tête PDF - Tenes)" value={brandingForm.pdfHeaderTenes.dateLabel} onChange={(e) => onHeaderTenesChange('dateLabel', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>Calcul des congés</Typography>
            <Stack direction="row" spacing={4} alignItems="center">
              <FormControlLabel
                control={<Switch checked={!!brandingForm.countWeekends} onChange={(e) => onBrandingChange('countWeekends', e.target.checked)} />}
                label="Compter les week-ends"
              />
              <FormControlLabel
                control={<Switch checked={!!brandingForm.countHolidays} onChange={(e) => onBrandingChange('countHolidays', e.target.checked)} />}
                label="Compter les jours fériés"
              />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button variant="contained" onClick={saveBranding} disabled={loading} startIcon={<SaveIcon />}>
                Enregistrer Branding
              </Button>
              <Button variant="outlined" onClick={loadBranding} disabled={loading}>
                Réinitialiser
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Numérotation automatique
                </Typography>
                
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    label="Prochain numéro d'ordre Mission"
                    type="number"
                    value={numberingForm.nextMissionOrderNumber}
                    onChange={onNumberingChange('nextMissionOrderNumber')}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                    helperText={`Ce numéro sera formaté comme ${String(numberingForm.nextMissionOrderNumber).padStart(4, '0')}/${new Date().getFullYear()} et incrémenté automatiquement`}
                  />

                  <TextField
                    fullWidth
                    label="Prochain numéro de Titre de congé"
                    type="number"
                    value={numberingForm.nextLeaveReferenceNumber}
                    onChange={onNumberingChange('nextLeaveReferenceNumber')}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                    helperText={`Ce numéro sera formaté comme ${String(numberingForm.nextLeaveReferenceNumber).padStart(4, '0')}/${new Date().getFullYear()} et incrémenté automatiquement`}
                  />

                  <TextField
                    fullWidth
                    label="Prochain numéro de contrat"
                    type="number"
                    value={numberingForm.nextContractNumber}
                    onChange={onNumberingChange('nextContractNumber')}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 3 }}
                    helperText={`Prochaine référence: ${numberingForm.numerotationAutomatique || '0001/DG/2MPI/2026'}`}
                  />

                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Références des rapports
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Prochain numéro - Attestation de travail"
                    type="number"
                    value={numberingForm.reportReferences.attestation_travail}
                    onChange={onReportReferenceChange('attestation_travail')}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                    helperText="Format: XXXXDG/2MPI/2026 (ex: 0016DG/2MPI/2026)"
                  />

                  <TextField
                    fullWidth
                    label="Prochain numéro - Certificat de travail"
                    type="number"
                    value={numberingForm.reportReferences.certificat_travail}
                    onChange={onReportReferenceChange('certificat_travail')}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                    helperText="Format: XXXXDG/2MPI/2026 (ex: 0015DG/2MPI/2026)"
                  />

                  <TextField
                    fullWidth
                    label="Prochain numéro - Décision"
                    type="number"
                    value={numberingForm.reportReferences.decision}
                    onChange={onReportReferenceChange('decision')}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 3 }}
                    helperText="Format: XXXXDG/2MPI/2026 (ex: 0010DG/2MPI/2026)"
                  />

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveNumbering}
                      disabled={loading}
                    >
                      {loading ? 'Enregistrement...' : 'Enregistrer la numérotation'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={loadNumbering}
                      disabled={loading}
                    >
                      Réinitialiser
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: 'info.main', color: 'info.contrastText' }}>
              <Typography variant="h6" gutterBottom>
                ⚠️ Information importante
              </Typography>
              <Typography variant="body2" paragraph>
                • Les numéros sont générés au format 0016/2026 (numéro séquentiel/année)
              </Typography>
              <Typography variant="body2" paragraph>
                • Si vous modifiez un numéro qui existe déjà, vous serez notifié et le dernier numéro utilisé vous sera indiqué
              </Typography>
              <Typography variant="body2" paragraph>
                • Après chaque utilisation, le numéro est automatiquement incrémenté de +1
              </Typography>
              <Typography variant="body2">
                • Les ordres de mission et titres de congé utiliseront ces numéros respectivement avec le format complet
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            onClick={saveAll}
            disabled={loading}
            startIcon={<SaveIcon />}
            size="large"
          >
            Enregistrer tous les paramètres
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSettingsPage;
